import React, { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { geoMercator, geoEqualEarth } from "d3-geo";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check, RotateCcw, Target } from "lucide-react";

const MAP_CONFIGS = {
  india: {
    label: "India",
    description: "Class X India map: Mountains, plains, rivers, water bodies, passes, latitudes & cities.",
    topo: "/india.topo.json",
    projection: "geoMercator",
    projectionConfig: { center: [82, 22], scale: 1000 },
    width: 800,
    height: 500,
    defaultCenter: [82, 22],
    inverter: (zoom) => geoMercator().center([82, 22]).scale(1000 * zoom).translate([400, 250]),
  },
  world: {
    label: "World",
    description: "Class IX World map: Natural regions, oceans, seas, gulfs, straits, rivers, mountains & plateaus.",
    topo: "/world-110m.json",
    projection: "geoEqualEarth",
    projectionConfig: { scale: 160 },
    width: 900,
    height: 500,
    defaultCenter: [0, 15],
    inverter: (zoom) => geoEqualEarth().scale(160 * zoom).translate([450, 250]),
  },
};

export default function MapWork() {
  const [mapKind, setMapKind] = useState("india");
  const [items, setItems] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState(MAP_CONFIGS.india.defaultCenter);

  const cfg = MAP_CONFIGS[mapKind];

  useEffect(() => {
    setItems([]);
    setCompleted([]);
    setCurrentIdx(0);
    setFeedback(null);
    setZoom(1);
    setCenter(cfg.defaultCenter);
    api.get("/map/items", { params: { kind: mapKind } }).then((r) => setItems(r.data.items || []));
    api.get("/map/progress", { params: { kind: mapKind } }).then((r) => setCompleted(r.data.completed || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapKind]);

  const remaining = useMemo(() => items.filter((i) => !completed.includes(i.id)), [items, completed]);
  const current = remaining[currentIdx % (remaining.length || 1)] || null;

  const handleClick = async (evt) => {
    if (!current) return;
    const svg = evt.currentTarget instanceof SVGSVGElement
      ? evt.currentTarget
      : (evt.currentTarget.ownerSVGElement || evt.target.ownerSVGElement);
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const cursor = pt.matrixTransform(ctm.inverse());
    const projection = cfg.inverter(zoom);
    const lngLat = projection.invert([cursor.x, cursor.y]);
    if (!lngLat) return;
    const [lng, lat] = lngLat;
    try {
      const { data } = await api.post("/map/attempt", { target_id: current.id, lat, lng, kind: mapKind });
      setFeedback(data);
      if (data.correct) {
        toast.success(`✓ ${current.name} — within ${data.distance_km} km`);
        setCompleted((c) => [...c, current.id]);
        setTimeout(() => {
          setFeedback(null);
          setCurrentIdx((i) => i + 1);
        }, 1200);
      } else {
        toast.error(`Off by ${data.distance_km} km — try again`);
        setTimeout(() => setFeedback(null), 1500);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record attempt");
    }
  };

  const reset = () => {
    setCurrentIdx(0);
    setFeedback(null);
  };

  // Group completed by category for the sidebar
  const byCategory = useMemo(() => {
    const m = {};
    items.forEach((i) => {
      if (!completed.includes(i.id)) return;
      const c = i.category || "Other";
      if (!m[c]) m[c] = [];
      m[c].push(i);
    });
    return m;
  }, [items, completed]);

  return (
    <div className="space-y-6">
      <div>
        <span className="brimi-pill bg-brimi-sage/15 text-brimi-sage">Map Work</span>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-brimi-navyText mt-2">Tap to place</h1>
        <p className="text-brimi-navyText/70 mt-1">{cfg.description}</p>
      </div>

      <div className="inline-flex bg-brimi-creamSoft p-1 rounded-xl" role="tablist">
        {Object.entries(MAP_CONFIGS).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setMapKind(k)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              mapKind === k ? "bg-white shadow-sm text-brimi-navyText" : "text-brimi-navyText/65 hover:text-brimi-navyText"
            }`}
            data-testid={`map-tab-${k}`}
            role="tab"
            aria-selected={mapKind === k}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        <div className="brimi-card p-3 relative overflow-hidden">
          <div
            className={`relative bg-brimi-creamSoft/40 rounded-xl ${feedback?.correct === false ? "shake" : ""} ${
              feedback?.correct === true ? "pulse-correct" : ""
            }`}
            data-testid={`map-${mapKind}`}
          >
            <ComposableMap
              key={mapKind}
              projection={cfg.projection}
              projectionConfig={cfg.projectionConfig}
              width={cfg.width}
              height={cfg.height}
              style={{ width: "100%", height: "auto" }}
              onClick={handleClick}
            >
              <ZoomableGroup
                zoom={zoom}
                center={center}
                onMoveEnd={({ zoom: z, coordinates }) => {
                  setZoom(z);
                  setCenter(coordinates);
                }}
              >
                <Geographies geography={cfg.topo}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        style={{
                          default: { fill: "#FAF6EC", stroke: "#8A9A5B", strokeWidth: 0.5, outline: "none" },
                          hover: { fill: "#E9EBE0", stroke: "#8A9A5B", strokeWidth: 0.7, outline: "none" },
                          pressed: { fill: "#E9EBE0", outline: "none" },
                        }}
                      />
                    ))
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
          </div>
          <div className="flex items-center gap-2 mt-3 px-2">
            <button onClick={() => setZoom((z) => Math.max(1, z - 0.4))} className="brimi-btn-ghost text-xs" data-testid="zoom-out-btn">−</button>
            <button onClick={() => setZoom((z) => Math.min(6, z + 0.4))} className="brimi-btn-ghost text-xs" data-testid="zoom-in-btn">+</button>
            <button onClick={() => { setZoom(1); setCenter(cfg.defaultCenter); }} className="brimi-btn-ghost text-xs" data-testid="zoom-reset-btn">Reset view</button>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="brimi-card p-5">
            <div className="flex items-center gap-2 text-brimi-navyText/60 text-xs uppercase tracking-widest font-semibold">
              <Target size={14} /> Find this on the map
            </div>
            {current ? (
              <>
                <h2 className="font-heading font-semibold text-2xl text-brimi-navyText mt-2" data-testid="map-target-name">
                  {current.name}
                </h2>
                <p className="text-sm text-brimi-navyText/60 capitalize">{current.category}</p>
              </>
            ) : (
              <p className="text-brimi-navyText/70 mt-2">All done! 🎉</p>
            )}
            <button onClick={reset} className="brimi-btn-ghost mt-3 text-sm" data-testid="map-reset-btn">
              <RotateCcw size={14} /> Reset queue
            </button>
          </div>
          <div className="brimi-card p-5">
            <div className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/60 mb-2">Completed</div>
            <p className="text-3xl font-heading font-bold text-brimi-navyText" data-testid="map-completed-count">
              {completed.length}
              <span className="text-brimi-navyText/50 text-lg">/{items.length}</span>
            </p>
            {Object.keys(byCategory).length > 0 && (
              <div className="mt-3 space-y-3 max-h-64 overflow-auto pr-1">
                {Object.entries(byCategory).map(([cat, list]) => (
                  <div key={cat}>
                    <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/50 mb-1">{cat}</p>
                    <ul className="space-y-1 text-sm text-brimi-navyText/85">
                      {list.map((i) => (
                        <li key={i.id} className="flex items-center gap-2">
                          <Check size={13} className="text-brimi-sage flex-shrink-0" /> <span>{i.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
